/* eslint-disable no-undef */
import { fabric } from 'fabric';
import { useConfigurationStore } from '@/stores/configurationModule';
import { setAlphaToOne } from '@/utils/common';
export default function useObjectControlsEventListeners() {
  const $eventBus = inject('bus');
  const configurationStore = useConfigurationStore();
  const {
    setHoveredPanel,
    setActivePanel,
    setActiveObjects,
    setActiveCanvas,
    discardActiveObjects
  } = configurationStore;
  const {
    activePanel,
    activeCanvas,
    fabricCanvases,
    box
  } = storeToRefs(configurationStore);
  function getActivePanelSettings() {
    return {
      panelWidth: activePanel.value.widthPx,
      panelHeight: activePanel.value.heightPx,
      panelTop: activePanel.value.topPx,
      panelLeft: activePanel.value.leftPx,
      panelAngle: activePanel.value.angle,
      panelInsideAngle: activePanel.value.insideAngle
    };
  }
  const hasActivePanel = computed(() => activePanel.value !== null);
  $eventBus.$on('object-added', obj => {
    discardActiveObjects();
    activeCanvas.value.add(obj);
    activeCanvas.value.setActiveObject(obj);
  });
  $eventBus.$on('object-added-new', data => {
    const isInsideCanvasActive = activeCanvas.value === fabricCanvases.value[1];
    data.originX = 'center';
    data.originY = 'center';

    // data is fabric.Object
    // create a rect with same dimension as the object (as a bounding box)

    const dataOldWidth = data.width;
    const dataOldHeight = data.height;

    // check if there is an active panel
    if (activePanel.value?.name) {
      // Panel settings
      const {
        panelWidth,
        panelHeight,
        panelTop,
        panelLeft,
        panelAngle,
        panelInsideAngle
      } = getActivePanelSettings();
      const objSize = Math.min(panelHeight, panelWidth);
      data.top = panelTop + panelHeight / 2;
      data.left = panelLeft + panelWidth / 2;
      if (data.objectType === 'text' || data.objectType === 'img') {
        data.angle = isInsideCanvasActive ? panelInsideAngle : panelAngle;
      }
      if (data.objectType === 'circle') {
        data.radius = objSize / 2;
      }
      if (['triangle', 'circle', 'trapezoid', 'hexagon'].includes(data.objectType)) {
        data.width = objSize;
        data.height = objSize;
      }
      if (data.objectType === 'line') {
        data.width = panelWidth;
      }
      if (data.objectType === 'rectangle') {
        data.width = panelWidth;
        data.height = panelHeight;
      }
    } else {
      data.top = activeCanvas.value.height / 2;
      data.left = activeCanvas.value.width / 2;
    }
    if (data.objectType === 'text') {
      const canvasWrapper = activeCanvas.value ? activeCanvas.value.wrapperEl : fabricCanvases.value[0].wrapperEl;
      data.hiddenTextareaContainer = canvasWrapper;
    }
    const objectsOfSameType = activeCanvas.value._objects.filter(obj => obj.objectType === data.objectType);
    let objectThatMatchTypeAndPosition = objectsOfSameType.filter(obj => {
      return obj.top === data.top && obj.left === data.left;
    });
    while (objectThatMatchTypeAndPosition.length > 0) {
      data.top += 5;
      data.left += 5;
      objectThatMatchTypeAndPosition = objectsOfSameType.filter(obj => (obj.top && obj.left) === (data.top && data.left));
    }

    // SIZE ADJUSTMENT NOW HAVE TO BE MADE WITH SCALE INSTEAD OF WIDTH AND HEIGHT
    // OTHERWISE ONLY THE BOUNDING BOX WILL BE ADJUSTED
    // HERE I SET THE SCALE ACCORDING TO WHAT WE WANT THE FINAL WIDTH AND HEIGHT TO BE
    // AND THEN RETURN THE ORIGINAL WIDTH AND HEIGHT TO RESET THE BOUNDING BOX
    // @TODO: THINK OF MORE ELEGANT WAY TO SOLVE THIS
    // _________________________________________________
    // This includes SVG objects
    if (!['image', 'img', 'text', 'i-text'].includes(data.objectType)) {
      let scaleX = 1;
      let scaleY = 1;
      if (activePanel.value?.name) {
        scaleX = data.width / dataOldWidth;
        scaleY = data.height / dataOldHeight;
      } else {
        const {
          width: canvasWidth,
          height: canvasHeight
        } = fabricCanvases.value[0];
        const largerCanvasDimension = Math.max(canvasWidth, canvasHeight);
        data.width = largerCanvasDimension / 10;
        data.height = largerCanvasDimension / 10;

        // Reset for height for line, all other objects are squares
        if (data.objectType === 'line') {
          data.height = dataOldHeight;
        }
        scaleX = data.width / dataOldWidth;
        scaleY = data.height / dataOldHeight;
      }
      data.scaleX = scaleX;
      data.scaleY = scaleY;
      data.height = dataOldHeight;
      data.width = dataOldWidth;
    }

    // Used to correct the position of object on inside canvas (x-flipping)
    // @TODO: Rethink, this is cumbersome
    // @NOTE: X Flip
    if (isInsideCanvasActive) {
      data.left = activeCanvas.value.width - data.left;
    }

    // @TODO: discard other selected objects

    // _________________________________________________
    $eventBus.$emit('object-added', data);
  });

  // Object manipulation
  $eventBus.$on('object-rotated', () => {
    const activeObject = activeCanvas.value.getActiveObject();
    const angle = activeObject.angle;
    if (angle >= 0 && angle < 90) {
      activeObject.angle = 90;
    } else if (angle >= 90 && angle < 180) {
      activeObject.angle = 180;
    } else if (angle >= 180 && angle < 270) {
      activeObject.angle = 270;
    } else {
      activeObject.angle = 0;
    }
    activeCanvas.value.fire('object:modified');
  });
  $eventBus.$on('object-duplicated', () => {
    let _clipboard = null;
    if (!activeCanvas.value) {
      setActiveCanvas(fabricCanvases.value[0]);
    }
    activeCanvas.value.getActiveObjects().forEach(object => {
      object.clone(function (cloned) {
        _clipboard = cloned;
        _clipboard.clone(function (clonedObj) {
          const OFFSET = 50;
          activeCanvas.value.discardActiveObject();
          clonedObj.set({
            left: clonedObj.left + OFFSET,
            top: clonedObj.top + OFFSET,
            // @NOTE: This is here to fix bug where when iText is clone while in
            // editing mode selectable becomes false
            selectable: true
          });
          clonedObj._controlsVisibility = object._controlsVisibility;
          if (clonedObj.type === 'activeSelection') {
            clonedObj.canvas = canvas; // @NOTE: Odakle ovaj canvas ovde?
            clonedObj.forEachObject(function (obj) {
              activeCanvas.value.add(obj);
            });
            clonedObj.setCoords();
          } else {
            activeCanvas.value.add(clonedObj);
          }
          _clipboard.top += OFFSET - 10;
          _clipboard.left += OFFSET - 10;
          activeCanvas.value.setActiveObject(clonedObj);
          activeCanvas.value.requestRenderAll();
        });
      });
    });
  });
  $eventBus.$on('objects-removed', () => {
    if (fabricCanvases?.value.length === 0) return;
    fabricCanvases.value.forEach(canvas => {
      if (canvas?.getActiveObjects().length === 0) return;
      canvas.getActiveObjects().forEach(obj => {
        canvas.remove(obj);
      });
      canvas._activeObject = null;
      setActiveObjects(canvas.getActiveObjects());
    });
  });

  // Object styling
  $eventBus.$on('background-changed', data => {
    if (data.includes('rgba')) {
      data = setAlphaToOne(data);
    }
    activeCanvas.value._objects.forEach(object => {
      if (object && object.id === 'background') {
        object.set('fill', data);
      }
    });
    activeCanvas.value.fire('object:modified');
  });
  $eventBus.$on('object-color-changed', data => {
    activeCanvas.value.getActiveObjects().forEach(obj => {
      if (obj.fill) {
        obj.set('fill', data);
      }
      if (obj.stroke) {
        obj.set('stroke', data);
      }
    });
    activeCanvas.value.fire('object:modified');
  });

  // Text styling --------------------------------------------------------------
  const AVAILABLE_TEXT_EVENTS_AND_STYLES = {
    'font-changed': 'fontFamily',
    'font-size-changed': 'fontSize',
    'font-color-changed': 'fill',
    'bold-toggled': 'fontWeight',
    'italic-toggled': 'fontStyle',
    'text-align-changed': 'textAlign'
  };
  function styleText(data, type) {
    if (!(type in AVAILABLE_TEXT_EVENTS_AND_STYLES) || !activeCanvas.value) return;
    const activeObjects = activeCanvas.value.getActiveObjects();
    if (!activeObjects || !activeObjects.length) return;
    activeObjects.forEach(obj => {
      let applyValue = '';
      switch (type) {
        case 'font':
        case 'font-changed':
        case 'font-size':
        case 'fontSize':
        case 'font-size-changed':
        case 'font-color':
        case 'text-align-changed':
        case 'font-color-changed':
          applyValue = data;
          break;
        case 'bold':
        case 'bold-toggled':
          applyValue = data ? 'bold' : 'normal';
          break;
        case 'italic':
        case 'italic-toggled':
          applyValue = data ? 'italic' : 'normal';
          break;
      }
      const key = AVAILABLE_TEXT_EVENTS_AND_STYLES[type];
      obj.set(key, applyValue);
    });
    activeCanvas.value.fire('object:modified');
  }
  Object.keys(AVAILABLE_TEXT_EVENTS_AND_STYLES).forEach(event => {
    $eventBus.$on(event, data => {
      styleText(data, event);
    });
  });

  // Object positioning --------------------------------------------------------
  $eventBus.$on('center-selection', () => {
    if (hasActivePanel.value) {
      const {
        panelWidth: _panelWidth,
        panelHeight: _panelHeight,
        panelTop: _panelTop,
        panelLeft: _panelLeft
      } = getActivePanelSettings();
      const panelWidth = _panelWidth / 2;
      const panelHeight = _panelHeight / 2;
      const panelTop = _panelTop / 2;
      const panelLeft = _panelLeft / 2;
      const selection = activeCanvas.value.getActiveObject();
      selection.originX = 'center';
      selection.originY = 'center';
      selection.top = panelTop + panelHeight / 2;
      selection.left = panelLeft + panelWidth / 2;
    } else {
      activeCanvas.value.getActiveObject().center();
    }
    activeCanvas.value.fire('object:modified');
  });
  function moveSelectionForward() {
    const activeObject = activeCanvas.value.getActiveObject();
    if (!activeObject) return;
    activeCanvas.value.bringForward(activeObject);
  }
  function moveSelectionBackward() {
    const activeObject = activeCanvas.value.getActiveObject();
    if (!activeObject) return;
    activeCanvas.value.sendBackwards(activeObject);
  }
  function moveSelectionToFront() {
    const activeObjects = activeCanvas.value.getActiveObjects();
    if (activeObjects.length > 0) {
      const maxZ = activeCanvas.value.getObjects().length;
      activeObjects.forEach(function (obj) {
        obj.moveTo(maxZ);
      });
    }
  }
  function moveSelectionToBack() {
    // @NOTE: Why not use only getActiveObjects?
    const activeObject = activeCanvas.value.getActiveObject();
    const activeObjects = activeObject ? [activeObject] : activeCanvas.value.getActiveObjects();
    if (activeObjects.length) {
      const objects = activeCanvas.value.getObjects().reverse();
      activeObjects.sort((a, b) => objects.indexOf(b) - objects.indexOf(a)).forEach(obj => obj.sendToBack());
    }
  }
  function alignToSelection(canvas, selection, alignDirection) {
    const objects = selection._objects;

    // No need to align if one object is selected
    if (!objects) {
      return;
    }

    // Calculate the bounds of the selection
    const selectionLeft = selection.left;
    const selectionTop = selection.top;
    const selectionRight = selectionLeft + selection.width * selection.scaleX;
    const selectionBottom = selectionTop + selection.height * selection.scaleY;
    const selectionCenterX = (selectionLeft + selectionRight) / 2;
    const selectionCenterY = (selectionTop + selectionBottom) / 2;
    canvas.discardActiveObject();
    const leftPropDirections = ['left', 'right', 'center'];
    let value = 0;
    let prop = leftPropDirections.includes(alignDirection) ? 'left' : 'top';
    objects.forEach(obj => {
      switch (alignDirection) {
        case 'left':
          value = selectionLeft + obj.getScaledWidth() / 2;
          break;
        case 'right':
          value = selectionRight - obj.getScaledWidth() / 2;
          break;
        case 'center':
          value = selectionCenterX;
          break;
        case 'top':
          value = selectionTop + obj.getScaledHeight() / 2;
          break;
        case 'bottom':
          value = selectionBottom - obj.getScaledHeight() / 2;
          break;
        case 'middle':
          value = selectionCenterY;
          break;
        default:
          console.error('Invalid alignment type');
          return;
      }
      obj.set(prop, value);
    });
    const sel = new fabric.ActiveSelection(objects, {
      canvas: canvas
    });
    canvas.setActiveObject(sel);

    // QUICK fix for bug that keeps moving selection of no panel is active
    sel.set('left', selectionLeft);
    sel.set('top', selectionTop);

    // Update the canvas
    canvas.requestRenderAll();
  }
  function alignSelectionToPanel(selection, alignDirection) {
    const isInsideCanvasActive = activeCanvas.value === fabricCanvases.value[1];
    const {
      panelWidth,
      panelLeft,
      panelHeight,
      panelTop
    } = getActivePanelSettings();
    const {
      width: selectionWidth,
      height: selectionHeight
    } = selection.getBoundingRect(true, false);
    const panelCenterX = panelLeft + panelWidth / 2;
    const panelCenterY = panelTop + panelHeight / 2;
    const xAdjustment = selectionWidth / 2;
    const yAdjustment = selectionHeight / 2;

    // conditions on 'left' and 'right' are because they are flipped on inside canvas
    const leftPropDirections = ['left', 'right', 'center'];
    let value = 0;
    let prop = leftPropDirections.includes(alignDirection) ? 'left' : 'top';
    switch (alignDirection) {
      case 'left':
        value = isInsideCanvasActive ? panelLeft + panelWidth - selectionWidth + xAdjustment : panelLeft + xAdjustment;
        break;
      case 'right':
        value = isInsideCanvasActive ? panelLeft + xAdjustment : panelLeft + panelWidth - selectionWidth + xAdjustment;
        break;
      case 'center':
        value = panelCenterX;
        break;
      case 'top':
        value = panelTop + yAdjustment;
        break;
      case 'bottom':
        value = panelTop + panelHeight - selectionHeight + yAdjustment;
        break;
      case 'middle':
        value = panelCenterY;
        break;
      default:
        console.error('Invalid alignment type');
        return;
    }

    // @NOTE: X Flip
    if (isInsideCanvasActive && leftPropDirections.includes(alignDirection)) {
      value = activeCanvas.value.width - value;
    }
    selection.set(prop, value);
  }
  function align(side) {
    alignToSelection(activeCanvas.value, activeCanvas.value.getActiveObject(), side);
    if (hasActivePanel.value) {
      alignSelectionToPanel(activeCanvas.value.getActiveObject(), side);
    }
    activeCanvas.value.fire('object:modified');
  }
  const ALIGNMENT = {
    'center-selection-h': 'center',
    'align-left-selection-h': 'left',
    'align-right-selection-h': 'right',
    'center-selection-v': 'middle',
    'align-top-selection-v': 'top',
    'align-bottom-selection-v': 'bottom'
  };
  Object.keys(ALIGNMENT).forEach(eventName => {
    $eventBus.$on(eventName, () => {
      align(ALIGNMENT[eventName]);
    });
  });
  function moveBackgroundToBack() {
    activeCanvas.value._objects.forEach(object => {
      if (object && object.id === 'background') {
        object.sendToBack();
      }
    });
  }
  $eventBus.$on('move-forward', () => {
    moveSelectionForward();
    activeCanvas.value.fire('object:modified');
  });
  $eventBus.$on('move-backward', () => {
    moveSelectionBackward();
    moveBackgroundToBack();
    activeCanvas.value.fire('object:modified');
  });
  $eventBus.$on('bring-to-front', () => {
    moveSelectionToFront();
    activeCanvas.value.fire('object:modified');
  });
  $eventBus.$on('move-to-back', () => {
    moveSelectionToBack();
    moveBackgroundToBack();
    activeCanvas.value.fire('object:modified');
  });
  $eventBus.$on('clear-editor-objects', () => {
    // TEMP will be covered by clearing configuration object
    setHoveredPanel('');
    setActivePanel(null);
    setActiveObjects([]);
  });
  $eventBus.$on('panel-selected', data => {
    // @TODO: Handel selection of inside panel, used in 3D mouse select composable
    setActivePanel(box.value.panels[box.value.panels.findIndex(b => b.name === data)]);
  });
}